import { Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { OLButton } from "OLButton";
import { OLPaper } from "OLPaper";
import { EmptyState } from "components/EmptyState/EmptyState";
import { ReactComponent as RuleIcon } from "components/Icons/ruleIcon.svg";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { FieldArray } from "formik";
import { useMessage } from "hooks/intl";
import { memo } from "react";
import {
  CampaignCondition,
  CampaignRuleTargetType,
  ReferralCampaignRule as ReferralCampaignRulesType,
  SelectedSchemaCondition,
  TransactionItem,
} from "types/campaigns";
import { Rule, RuleError } from "../Rule/Rule";
import { checkErrorsInRules } from "../utils";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    error: {
      color: theme.red500,
      fontSise: "14px",
      marginBottom: theme.spacing(8),
      fontWeight: 500,
    },
    subtitle: {
      fontSize: 14,
      fontWeight: 500,
      marginBottom: theme.spacing(8),
    },
    rule: {
      marginBottom: theme.spacing(12),
    },
    rulesWrapper: {
      marginBottom: theme.spacing(12),
    },
    buttonWrapper: {
      display: "flex",
      justifyContent: "flex-start",
    },
    flexEnd: {
      justifyContent: "flex-end",
    },
    displayNone: {
      display: "none",
      "&.error-edit-display": {
        display: "block",
      },
    },
  }),
);

export const ReferralCampaignRules = memo(
  function ReferralCampaignRules(props: {
    conditions: CampaignCondition[];
    selectedSchemaConditions?: SelectedSchemaCondition[];
    errors?: any;
    rules: ReferralCampaignRulesType[];
    transactionItemsFilters?: TransactionItem[];
  }) {
    const classes = useStyles();
    const { message } = useMessage();

    const isGeneralError =
      props.errors?.rules &&
      Array.isArray(props.errors.rules) &&
      typeof props.errors?.rules[0] === "string";
    const areErrorsInRules = !isGeneralError
      ? checkErrorsInRules(props.errors)
      : false;
    const areReferrerRules = props.rules.some(
      (item) => item.target === "referrer",
    );
    const areRefereeRules = props.rules.some(
      (item) => item.target === CampaignRuleTargetType.SELF,
    );
    const maxRulesCount = 6;
    const isMaxRulesCount = props.rules.length >= maxRulesCount;

    return (
      <>
        <Typography fontSize="16px" mb="16px" fontWeight={500}>
          {message({ id: "rules" })}
        </Typography>
        {areErrorsInRules && (
          <Typography
            data-test-id="campaign-rules-error"
            className={`${classes.error} Mui-error`}
          >
            {message(
              { id: "thereIsAnErrorInRule" },
              {
                type: message({ id: "referral" }),
              },
            )}
          </Typography>
        )}
        {isGeneralError && (
          <Typography
            className={`${classes.error} Mui-error`}
            data-test-id="campaign-rules-error-general"
          >
            {props.errors.rules[0]}
          </Typography>
        )}
        <Typography
          data-test-id="campaign-rules-error-general-edit"
          id="data-rules-edit"
          className={`${classes.error} ${classes.displayNone} Mui-error`}
        >
          {message({ id: "toSaveCampaignFinishEditingRules" })}
        </Typography>

        <div data-test-id="campaign-rules" className={classes.rulesWrapper}>
          <FieldArray
            name="rules"
            render={({ push, remove, form }) => {
              const { errors, setFieldError } = form;

              return (
                <>
                  <Typography className={classes.subtitle}>
                    {message({ id: "rulesForReferrer" })}
                  </Typography>
                  <OLPaper className={classes.rule}>
                    <div data-test-id="rules-referrer-container">
                      {props.rules.map((rule, index) => {
                        const currentError =
                          errors.rules && (errors.rules as [])[index];

                        // Display rules for referrer
                        if (rule.target === CampaignRuleTargetType.SELF)
                          return null;
                        const referrerConditions = props.conditions.filter(
                          (condition) =>
                            condition.label !== "refereeExpression",
                        );

                        return (
                          <Rule
                            key={index}
                            index={index}
                            addRule={push}
                            removeRule={remove}
                            rule={rule}
                            conditions={referrerConditions}
                            error={currentError as RuleError}
                            selectedSchemaConditions={
                              props.selectedSchemaConditions
                            }
                            isMaxRulesCount={isMaxRulesCount}
                            transactionItemsFilters={
                              props.transactionItemsFilters
                            }
                          />
                        );
                      })}
                      {areReferrerRules ? (
                        <div
                          className={`${classes.buttonWrapper} ${classes.flexEnd}`}
                        >
                          <OLButton
                            color="primary"
                            data-test-id="rules-referrer-add"
                            onClick={() => {
                              if (isGeneralError) {
                                setFieldError("rules", undefined);
                              }

                              push({ target: "referrer" });
                            }}
                            variant="contained"
                            size="small"
                            disabled={isMaxRulesCount}
                          >
                            {message({ id: "adddNewRule" })}
                          </OLButton>
                        </div>
                      ) : (
                        <EmptyState
                          icon={<RuleIcon />}
                          title={message({
                            id: "addRulesAndConfigureConditionsAndEffects",
                          })}
                          description={message({ id: "addRulesDescription" })}
                          button={
                            <OLButton
                              color="primary"
                              data-test-id={"rules-referrer-add"}
                              onClick={() => {
                                if (isGeneralError) {
                                  setFieldError("rules", undefined);
                                }

                                push({ target: "referrer" });
                              }}
                              variant="contained"
                              size="medium"
                              disabled={isMaxRulesCount}
                            >
                              {message({ id: "adddNewRule" })}
                            </OLButton>
                          }
                        />
                      )}
                    </div>
                  </OLPaper>

                  <Typography className={classes.subtitle}>
                    {message({ id: "rulesForReferee" })}
                  </Typography>
                  <OLPaper>
                    <div data-test-id="rules-referee-container">
                      {props.rules.map((rule, index) => {
                        const currentError =
                          errors.rules && (errors.rules as [])[index];
                        // Display rules for referee
                        if (rule.target === "referrer") return null;
                        const referreConditions = props.conditions.filter(
                          (condition) =>
                            condition.label !== "referrerExpression",
                        );

                        return (
                          <Rule
                            key={index}
                            index={index}
                            addRule={push}
                            removeRule={remove}
                            rule={rule}
                            conditions={referreConditions}
                            error={currentError as RuleError}
                            selectedSchemaConditions={
                              props.selectedSchemaConditions
                            }
                            isMaxRulesCount={isMaxRulesCount}
                            transactionItemsFilters={
                              props.transactionItemsFilters
                            }
                          />
                        );
                      })}
                      {areRefereeRules ? (
                        <div
                          className={`${classes.buttonWrapper} ${classes.flexEnd}`}
                        >
                          <OLButton
                            color="primary"
                            data-test-id="rules-referee-add"
                            onClick={() => {
                              if (isGeneralError) {
                                setFieldError("rules", undefined);
                              }

                              push({ target: CampaignRuleTargetType.SELF });
                            }}
                            variant="contained"
                            size="small"
                            disabled={isMaxRulesCount}
                          >
                            {message({ id: "adddNewRule" })}
                          </OLButton>
                        </div>
                      ) : (
                        <EmptyState
                          icon={<RuleIcon />}
                          title={message({
                            id: "addRulesAndConfigureConditionsAndEffects",
                          })}
                          description={message({ id: "addRulesDescription" })}
                          button={
                            <OLButton
                              color="primary"
                              data-test-id={"rules-referee-add"}
                              onClick={() => {
                                if (isGeneralError) {
                                  setFieldError("rules", undefined);
                                }

                                push({ target: CampaignRuleTargetType.SELF });
                              }}
                              variant="contained"
                              size="medium"
                              disabled={isMaxRulesCount}
                            >
                              {message({ id: "adddNewRule" })}
                            </OLButton>
                          }
                        />
                      )}
                    </div>
                  </OLPaper>
                </>
              );
            }}
          />
        </div>
      </>
    );
  },
);
